import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Gamepad2, Heart, Coins, Trophy, ArrowLeft,
  RefreshCw, CheckCircle, XCircle, Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const GamePlayer = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { toast } = useToast();
  
  const [session, setSession] = useState(null);
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameStatus, setGameStatus] = useState('playing'); // playing, won, lost
  const [endingGame, setEndingGame] = useState(false);
  const [result, setResult] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);

  useEffect(() => {
    loadSession();
    fetchGameDetails();
  }, [slug]);

  useEffect(() => {
    // Timer for tracking play time
    let timer;
    if (gameStatus === 'playing' && session) {
      timer = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameStatus, session]);

  const loadSession = () => {
    const stored = localStorage.getItem('currentGameSession');
    if (stored) {
      const sessionData = JSON.parse(stored);
      if (sessionData.gameSlug === slug) {
        setSession(sessionData);
        setLives(sessionData.lives === -1 ? 999 : sessionData.lives);
      }
    }
    setLoading(false);
  };

  const fetchGameDetails = async () => {
    try {
      const res = await api.get(`/games/${slug}`);
      setGame(res.data.game);
    } catch (error) {
      console.error('Failed to fetch game:', error);
    }
  };

  const handleScoreUpdate = (newScore) => {
    setScore(newScore);
  };

  const handleLoseLife = () => {
    const newLives = lives - 1;
    setLives(newLives);
    
    if (newLives <= 0 && session?.lives !== -1) {
      endGame(false);
    }
  };

  const endGame = async (won) => {
    if (!session || endingGame) return;
    
    setEndingGame(true);
    setGameStatus(won ? 'won' : 'lost');

    try {
      const res = await api.post(`/games/${slug}/end`, {
        session_id: session.sessionId,
        score,
        won,
        time_played_seconds: timeElapsed
      });

      setResult(res.data);
      localStorage.removeItem('currentGameSession');
      
      toast({
        title: won ? "You Won!" : "Game Over",
        description: won && res.data.reward_earned > 0 
          ? `You earned ${res.data.reward_earned} AGP!` 
          : res.data.message,
      });

    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to end game",
        variant: "destructive"
      });
    } finally {
      setEndingGame(false);
    }
  };

  // Demo game controls (for testing without actual game)
  const simulateWin = () => {
    setScore(prev => prev + 1000);
    endGame(true);
  };

  const simulateLoss = () => {
    if (session?.difficulty === 'basic') {
      toast({ title: "Practice Mode", description: "No lives in practice mode!" });
      return;
    }
    handleLoseLife();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">No Active Session</h2>
            <p className="text-muted-foreground mb-4">
              Please start a game from the Game Zone
            </p>
            <Button onClick={() => navigate('/games')}>
              <Gamepad2 className="w-4 h-4 mr-2" />
              Go to Game Zone
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Game Over Screen
  if (gameStatus !== 'playing' && result) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="game-result-screen">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            {gameStatus === 'won' ? (
              <>
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-10 h-10 text-yellow-400" />
                </div>
                <h2 className="text-2xl font-bold text-green-400 mb-2">You Won!</h2>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-10 h-10 text-red-400" />
                </div>
                <h2 className="text-2xl font-bold text-red-400 mb-2">Game Over</h2>
              </>
            )}

            <div className="space-y-3 my-6">
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>Score</span>
                <span className="font-bold">{result.score}</span>
              </div>
              {result.reward_earned > 0 && (
                <div className="flex justify-between p-3 bg-yellow-500/20 rounded-lg">
                  <span className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-yellow-400" />
                    Reward
                  </span>
                  <span className="font-bold text-yellow-400">+{result.reward_earned} AGP</span>
                </div>
              )}
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>New Balance</span>
                <span className="font-bold text-yellow-400">{result.new_agp_balance?.toFixed(0)} AGP</span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => navigate('/games')}
              >
                <Home className="w-4 h-4 mr-2" />
                Game Zone
              </Button>
              <Button 
                className="flex-1"
                onClick={() => {
                  setGameStatus('playing');
                  setResult(null);
                  setScore(0);
                  setTimeElapsed(0);
                  navigate('/games');
                }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="game-player-page">
      {/* Game HUD */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                if (confirm('Exit game? Progress will be lost.')) {
                  endGame(false);
                  navigate('/games');
                }
              }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Exit
            </Button>

            <div className="flex items-center gap-4">
              {/* Score */}
              <div className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-full">
                <Trophy className="w-4 h-4 text-yellow-400" />
                <span className="font-bold" data-testid="game-score">{score}</span>
              </div>

              {/* Lives */}
              {session.lives !== -1 && (
                <div className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-full">
                  <Heart className="w-4 h-4 text-red-400" />
                  <span className="font-bold" data-testid="game-lives">{lives}</span>
                </div>
              )}

              {/* Time */}
              <div className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-full">
                <span className="text-sm text-muted-foreground">
                  {Math.floor(timeElapsed / 60)}:{(timeElapsed % 60).toString().padStart(2, '0')}
                </span>
              </div>
            </div>

            <div className="text-right">
              <p className="text-xs text-muted-foreground capitalize">{session.difficulty}</p>
              <p className="text-sm font-medium">{game?.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Game Area */}
      <div className="max-w-4xl mx-auto p-4">
        <Card className="aspect-video bg-gradient-to-br from-purple-500/10 to-pink-500/10 flex items-center justify-center">
          <CardContent className="text-center">
            {game?.game_url ? (
              <iframe
                src={game.game_url}
                className="w-full h-full border-0"
                title={game.name}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
              />
            ) : (
              <>
                <Gamepad2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">{game?.name}</h3>
                <p className="text-muted-foreground mb-4">
                  Game integration coming soon!
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  For now, use the demo controls below to test the reward system.
                </p>

                {/* Demo Controls */}
                <div className="flex gap-3 justify-center">
                  <Button onClick={() => setScore(prev => prev + 100)} variant="outline">
                    +100 Score
                  </Button>
                  <Button onClick={simulateLoss} variant="outline" className="text-red-400">
                    <Heart className="w-4 h-4 mr-2" />
                    Lose Life
                  </Button>
                  <Button onClick={simulateWin} className="bg-green-500 hover:bg-green-600">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Win Game
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Game Info */}
        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium mb-2">How to Play</h4>
          <p className="text-sm text-muted-foreground">
            {game?.instructions || 'Instructions not available'}
          </p>
          {game?.controls && (
            <>
              <h4 className="font-medium mt-4 mb-2">Controls</h4>
              <p className="text-sm text-muted-foreground">{game.controls}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GamePlayer;
